import {ParamComponent} from 'panel';
import {produce} from 'immer';
import clamp from 'lodash/clamp';
import sum from 'lodash/sum';
import isEqual from 'lodash/isEqual';
import partition from 'lodash/partition';
import {VNode} from 'snabbdom';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  CustomMultiAttribution,
  MultiAttribution,
  MultiAttributionCancelEvent,
  MultiAttributionChangeEvent,
  MultiAttributionDeleteEvent,
  MultiAttributionSection,
  MultiAttributionSectionItem,
  MultiAttributionType,
  MultiAttributionWeights,
  RemoveAttributionValue,
} from 'common/widgets/multi-attribution-menu/types';
import {AttributionTypeToSpec, CustomDefaultWeights} from 'common/widgets/multi-attribution-menu/constants';
import {InputChangeEvent} from 'common/components/input/types';
import {parseNumber} from 'common/util';
import {reporter} from 'common/report/error-reporting';

import {template} from './template';
import css from './index.inline.styl';

type MultiAttributionMenuState = {
  focusedAttributionType: MultiAttributionType;
  stagedMultiAttribution: MultiAttribution;
};

type MultiAttributionMenuParams = {
  multiAttribution: MultiAttribution;
  metricIndex: number;
  handleCancel?: (ev: MultiAttributionCancelEvent) => void;
  handleChange?: (ev: MultiAttributionChangeEvent) => void;
  handleDelete?: (ev: MultiAttributionDeleteEvent) => void;
};

type MultiAttributionMenuJsxProps = {
  params?: MultiAttributionMenuParams;
};

export class MultiAttributionMenu extends ParamComponent<MultiAttributionMenuParams, MultiAttributionMenuState> {
  _tooltipPositionParentElement: HTMLElement;

  get config() {
    return Object.assign(super.config, {
      css,
      template,
      defaultState: {},
      useShadowDom: true,
      params: {
        multiAttribution: {
          type: Object,
          required: true,
        },
        metricIndex: {
          type: Number,
          required: true,
        },
        handleCancel: Function,
        handleChange: Function,
        handleDelete: Function,
      },
      hooks: {
        preUpdate: (_newState, newParams?: MultiAttributionMenuParams) => {
          if (newParams && !isEqual(newParams.multiAttribution, this.params.multiAttribution)) {
            this.handleParamsUpdate(newParams);
          }
        },
      },
    });
  }

  connectedCallback() {
    this.handleParamsUpdate(this.params);
    super.connectedCallback();
  }

  handleParamsUpdate(params: MultiAttributionMenuParams): void {
    this.update({
      stagedMultiAttribution: params.multiAttribution,
    });

    // Handle setting the default focused section attribution type so the DropMenu
    // is already open to the current selected attribution type.
    const setDefaultFocusedAttributionType = !this.state.focusedAttributionType;
    if (setDefaultFocusedAttributionType) {
      this.update({
        focusedAttributionType: params.multiAttribution.type,
      });
    }
  }

  getAttributionTypeSections(): MultiAttributionSection[] {
    const multiAttributionItems: MultiAttributionSectionItem[] = [
      MultiAttributionType.LastTouch,
      MultiAttributionType.FirstTouch,
      MultiAttributionType.Linear,
      MultiAttributionType.UShaped,
      MultiAttributionType.JShaped,
      MultiAttributionType.InverseJShaped,
      MultiAttributionType.Custom,
    ].map((attributionType) => {
      return {
        icon: AttributionTypeToSpec[attributionType].icon,
        label: AttributionTypeToSpec[attributionType].label,
        value: attributionType,
        isSelected: attributionType === this.state.stagedMultiAttribution?.type,
      };
    });

    const [predefinedItems, customItems] = partition(
      multiAttributionItems,
      (item) => item.value !== MultiAttributionType.Custom,
    );

    const removeAttributionItem: MultiAttributionSectionItem = {
      description: null,
      icon: `x`,
      isSelected: false,
      label: `Remove Attribution`,
      value: RemoveAttributionValue,
      // TODO: need this noop to prevent positioning issue with context panel DropMenu when initially opening the
      // the select menu.
      key: Date.now(),
    };
    return [
      {
        items: [...predefinedItems],
      },
      {
        items: [...customItems, removeAttributionItem],
      },
    ];
  }

  onRemoveAttribution() {
    this.dispatchDelete();
  }

  onAttributionTypeChange(newAttributionType: MultiAttributionType): void {
    this.updateStagedAttribution((draft) => {
      const oldAttributionType = draft.type;
      draft.type = newAttributionType;

      if (draft.type === MultiAttributionType.Custom) {
        const oldWeights = AttributionTypeToSpec[oldAttributionType].weights;
        draft.weights = oldWeights
          ? {...oldWeights}
          : // Default weights for custom model if change from a model without first/middle/last inputs
            CustomDefaultWeights;
        draft.name = this.getDefaultCustomName();
      } else {
        draft.weights = AttributionTypeToSpec[draft.type].weights;
      }
    });
  }

  onCustomNameChange(ev: InputChangeEvent): void {
    this.updateStagedAttribution((draft) => {
      if (draft.type === MultiAttributionType.Custom) {
        draft.name = ev.detail.value || this.getDefaultCustomName();
      } else {
        reporter.error(`onCustomNameChange: unexpected MultiAttributionType`, {type: draft.type});
      }
    });
  }

  getDefaultCustomName(): string {
    return `Custom model ${this.params.metricIndex + 1}`;
  }

  setTooltipPositionParentElement(vnode: VNode) {
    this._tooltipPositionParentElement = vnode.elm as HTMLElement;
  }
  getTooltipPositionParentElement() {
    return this._tooltipPositionParentElement;
  }

  getWeightsForAttributionType(attributionType: MultiAttributionType): MultiAttributionWeights | undefined {
    return AttributionTypeToSpec[attributionType].weights;
  }

  getWeights(): MultiAttributionWeights {
    const {stagedMultiAttribution} = this.state;
    return stagedMultiAttribution.type === MultiAttributionType.Custom
      ? stagedMultiAttribution.weights
      : this.getWeightsForAttributionType(stagedMultiAttribution.type);
  }

  onWeightChange({ev, weightIndex}: {ev: InputChangeEvent; weightIndex: number}): void {
    const newWeight = clamp(parseNumber(ev.detail.value) ?? 0, 0, 100);

    /** This block of logic will split the remaining weight proportionally among the non-modified weight entries*/
    const oldWeights = this.getWeights();
    const remainingWeightSpecs: Array<{weight: number; index: number}> = [
      oldWeights.first,
      oldWeights.middle,
      oldWeights.last,
    ]
      .map((weight, index) => ({weight, index}))
      .filter(({index}) => index !== weightIndex);
    const oldRemainingWeight = sum(remainingWeightSpecs.map(({weight}) => weight));
    const newRemainingWeight = 100 - newWeight;
    remainingWeightSpecs[0].weight =
      oldRemainingWeight === 0
        ? Math.floor(newRemainingWeight / 2)
        : Math.floor(newRemainingWeight * (remainingWeightSpecs[0].weight / oldRemainingWeight));
    remainingWeightSpecs[1].weight = 100 - newWeight - remainingWeightSpecs[0].weight;
    const newWeights: number[] = [];
    remainingWeightSpecs.forEach((spec) => {
      newWeights[spec.index] = spec.weight;
    });
    newWeights[weightIndex] = newWeight;

    this.updateStagedAttribution((draft) => {
      const partialUpdate: Partial<CustomMultiAttribution> = {
        weights: {
          first: newWeights[0],
          middle: newWeights[1],
          last: newWeights[2],
        },
      };
      if (draft.type !== MultiAttributionType.Custom) {
        partialUpdate.type = MultiAttributionType.Custom;
        partialUpdate.name = this.getDefaultCustomName();
      }
      Object.assign(draft, partialUpdate);
    });
  }

  updateFocusedAttributionType(item: MultiAttributionSectionItem): void {
    this.update({
      // Handle the Remove Attribution button case
      focusedAttributionType: item.value === RemoveAttributionValue ? null : item.value,
    });
  }

  updateStagedAttribution(updateFn: (_: MultiAttribution) => void) {
    this.update({
      stagedMultiAttribution: produce(this.state.stagedMultiAttribution, updateFn),
    });
  }

  dispatchChange() {
    const event: MultiAttributionChangeEvent = new CustomEvent(`change`, {
      detail: {multiAttribution: this.state.stagedMultiAttribution},
    });
    this.params?.handleChange(event);
  }

  dispatchCancel() {
    const event: MultiAttributionCancelEvent = new CustomEvent(`cancel`, {});
    this.params?.handleCancel(event);
  }

  dispatchDelete() {
    const event: MultiAttributionDeleteEvent = new CustomEvent(`delete`, {});
    this.params?.handleDelete(event);
  }
}

export const MultiAttributionMenuEl = defineAndMakeJsxFactory<MultiAttributionMenuJsxProps>(
  `mp-multi-attribution-menu`,
  MultiAttributionMenu,
);
export const MultiAttributionMenuReact = panel2react<MultiAttributionMenuJsxProps>(
  `mp-multi-attribution-menu`,
  MultiAttributionMenu,
);
